// food-taste-profile.js — the food-side taste-learning loop.
//
// Mirrors src/lib/taste-profile.js (coffee): a cook event with a 1–5 rating
// nudges per-cuisine and per-mood affinity scores, which decay over time and are
// injected into the recipe suggester so Tonight leans toward what Josh actually
// liked cooking. Plus an explicit free-text `avoid` for hard dislikes the model
// can't infer (e.g. "raw onions, cooked is fine").
//
// Persisted as preferences.food_taste jsonb (migration 20260718130000).

export const EMPTY_FOOD_TASTE = {
  cuisine_affinity: {}, // { [cuisine]: score in [-BOUND, BOUND] }
  mood_affinity: {},    // { [mood_tag]: score }
  avoid: "",            // explicit dislikes, free text
  sample_count: 0,
  last_cook_at: null,
  insights: {},
};

const STEP = 1;      // nudge magnitude per cook at full signal
const DECAY = 0.98;  // each update pulls existing scores gently toward zero
const BOUND = 6;     // clamp so no single cuisine dominates forever
const PRUNE = 0.15;  // drop near-zero scores to keep the object small

function r1(n) { return Math.round(n * 100) / 100; }
function clamp(n) { return Math.max(-BOUND, Math.min(BOUND, n)); }

// Map one cook event (rating 1–5 + the recipe's tags) to per-dimension deltas.
// rating 5 → +1 signal, 3 → 0 (neutral, no learning), 1 → -1.
export function cookEventToTasteDelta(rating, recipe) {
  const rt = Number(rating);
  if (!rt) return null;
  const signal = (rt - 3) / 2; // -1 .. +1
  if (signal === 0) return null;
  const cuisine = {}, mood = {};
  for (const c of recipe?.cuisine || []) if (c) cuisine[String(c).toLowerCase()] = signal * STEP;
  for (const m of recipe?.mood_tags || []) if (m) mood[String(m).toLowerCase()] = signal * STEP;
  return { cuisine, mood, signal };
}

// Fold a delta into the running profile: decay everything, add the delta, clamp,
// prune near-zero noise, bump sample_count. Pure — returns a fresh profile.
export function applyDeltaToFoodProfile(profile, delta, nowIso) {
  const p = { ...EMPTY_FOOD_TASTE, ...(profile || {}) };
  const decay = (obj) => {
    const out = {};
    for (const [k, v] of Object.entries(obj || {})) out[k] = r1(v * DECAY);
    return out;
  };
  const ca = decay(p.cuisine_affinity);
  const ma = decay(p.mood_affinity);
  if (delta) {
    for (const [k, v] of Object.entries(delta.cuisine || {})) ca[k] = r1(clamp((ca[k] || 0) + v));
    for (const [k, v] of Object.entries(delta.mood || {})) ma[k] = r1(clamp((ma[k] || 0) + v));
  }
  const prune = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => Math.abs(v) >= PRUNE));
  return {
    ...p,
    cuisine_affinity: prune(ca),
    mood_affinity: prune(ma),
    sample_count: (p.sample_count || 0) + 1,
    last_cook_at: nowIso || p.last_cook_at || null,
  };
}

// Rank affinities into liked/disliked lists for display + prompt building.
function split(affinity, n = 4) {
  const entries = Object.entries(affinity || {}).sort((a, b) => b[1] - a[1]);
  const liked = entries.filter(([, v]) => v >= 1).slice(0, n).map(([k]) => k);
  const disliked = entries.filter(([, v]) => v <= -1).slice(-n).map(([k]) => k);
  return { liked, disliked };
}

export function foodTasteSummary(profile) {
  const p = { ...EMPTY_FOOD_TASTE, ...(profile || {}) };
  const cuisine = split(p.cuisine_affinity);
  const mood = split(p.mood_affinity);
  return {
    likedCuisines: cuisine.liked,
    dislikedCuisines: cuisine.disliked,
    likedMoods: mood.liked,
    dislikedMoods: mood.disliked,
    avoid: (p.avoid || "").trim(),
    sampleCount: p.sample_count || 0,
    hasSignal:
      cuisine.liked.length || cuisine.disliked.length ||
      mood.liked.length || mood.disliked.length || !!(p.avoid || "").trim(),
  };
}

// Compact block for the recipe-suggester prompt (mirrors coffee's tasteBlock).
// Returns "" when there's nothing learned yet, so the prompt stays clean.
export function foodTasteBlock(profile) {
  const s = foodTasteSummary(profile);
  if (!s.hasSignal) return "";
  const lines = [];
  if (s.likedCuisines.length) lines.push(`Leans toward these cuisines: ${s.likedCuisines.join(", ")}.`);
  if (s.dislikedCuisines.length) lines.push(`Has rated these cuisines poorly: ${s.dislikedCuisines.join(", ")}.`);
  if (s.likedMoods.length) lines.push(`Enjoys ${s.likedMoods.join(", ")} dishes.`);
  if (s.dislikedMoods.length) lines.push(`Less keen on ${s.dislikedMoods.join(", ")} dishes.`);
  if (s.avoid) lines.push(`Hard dislikes / avoid: ${s.avoid}`);
  const basis = s.sampleCount ? ` (learned from ${s.sampleCount} rated cook${s.sampleCount === 1 ? "" : "s"})` : "";
  return `Taste profile${basis}:\n${lines.map((l) => `- ${l}`).join("\n")}`;
}
